import React, { useContext, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box, useTheme } from '@mui/material';
import { AlertMessage } from '@eposnow/ui-core';
import CategoryForm from './CategoryForm';
import validationSchema from '../helpers/validationSchema';
import { Category, CategoryCreateEdit, CategoryErrors } from '../../../../types';
import { UserContext } from '../../../../context/UserContext';
import { CATEGORIES_API_CACHE_KEY } from '../../../../constants';
import useCreateUpdate from '../../../../helperHooks/useCreateUpdate';
import { convertObjectToStringValues } from '../../../../helpers/helpers';
import CreateEditCard from '../../../../components/createEditComponents/CreateEditCard';
import { API_V1_HOSTNAME_KEY } from '../../../../localStorageKeys';

export const emptyObject: CategoryCreateEdit = {
  Id: null,
  ParentCategoryId: null,
  RootParentId: null,
  Name: '',
  Description: '',
  ImageUrl: '',
  PopupNoteId: '',
  IsWet: false,
  ShowOnTill: true,
  ReferenceCode: '',
  PopupNote: '',
  Level: null,
};

export type CreateEditCategoryCardProps = {
  currentObject: Category;
  resetObject: () => void;
  setSuccessMessage: (message: string) => void;
  errorMessage: string;
  setErrorMessage: (message: string) => void;
  errors: CategoryErrors;
  setErrors: (obj: any) => void;
  mode: string;
};

const CreateEditCategoryCard = ({
  currentObject,
  resetObject,
  setSuccessMessage,
  errorMessage,
  setErrorMessage,
  errors,
  setErrors,
  mode,
}: CreateEditCategoryCardProps) => {
  const [initialObject, setInitialObject] = useState<CategoryCreateEdit>(emptyObject);
  const [objectId, setObjectId] = useState<number>(0);
  const { t } = useTranslation();
  const theme = useTheme();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const { locale } = useContext(UserContext);

  const removeSavedObject = () => {
    const savedObjects = JSON.parse(sessionStorage.getItem(CATEGORIES_API_CACHE_KEY) || '{}');
    delete savedObjects[`${objectId}`];
    sessionStorage.setItem(CATEGORIES_API_CACHE_KEY, JSON.stringify(savedObjects));
  };

  const { isLoading, apiCall } = useCreateUpdate(
    mode,
    'products.categories',
    `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/categories`,
    'categories',
    CATEGORIES_API_CACHE_KEY,
    resetObject,
    removeSavedObject,
    setSuccessMessage,
    setErrorMessage,
    setErrors,
    mode === 'create' ? '0' : currentObject.id.toString()
  );

  useEffect(() => {
    const savedObjects = JSON.parse(sessionStorage.getItem(CATEGORIES_API_CACHE_KEY) || '{}');
    const newObjectId = mode === 'create' ? 0 : currentObject.Id;
    setObjectId(newObjectId);
    const savedObject = savedObjects[`${newObjectId}`];

    let newInitialObject: CategoryCreateEdit = { ...emptyObject, id: 0, ...savedObject };
    if (mode === 'create') {
      sessionStorage.setItem(
        CATEGORIES_API_CACHE_KEY,
        JSON.stringify({
          ...savedObjects,
          [`${newObjectId}`]: newInitialObject,
        })
      );
    } else {
      const parsedCurrentObject = { ...currentObject };
      newInitialObject = {
        ...parsedCurrentObject,
        Name: parsedCurrentObject.Name,
        IsWet: parsedCurrentObject.IsWet,
      };
    }

    setInitialObject(newInitialObject);
  }, [currentObject]);

  const formik = useFormik({
    initialValues: {
      ...initialObject,
      ParentCategoryId: initialObject?.ParentCategoryId?.toString() || '',
      IsWet: initialObject?.IsWet ? 'true' : '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values, actions) => {
      if (isLoading) return;

      const requestValues: Category = {
        ...initialObject,
        id: currentObject?.Id,
        Name: values.Name,
        IsWet: Boolean(values.IsWet),
        ParentCategoryId: parseInt(values.ParentCategoryId, 10) || null,
      };

      actions.setSubmitting(false);

      if (mode === 'create') {
        apiCall([requestValues]);
      } else {
        const oldObject: Category = {
          ...initialObject,
          id: initialObject?.Id,
          ParentCategoryId: initialObject?.ParentCategoryId,
        };
        if (
          JSON.stringify(convertObjectToStringValues(requestValues)) !==
          JSON.stringify(convertObjectToStringValues(oldObject))
        ) {
          apiCall({ oldObject, newObject: requestValues });
        }
      }
    },
  });

  const clearError = (errorKey: string) => {
    setErrors(({ [errorKey as keyof CategoryErrors]: _value, ...remaining }) => remaining);
  };

  useEffect(() => {
    if (formik.values?.Id == null) return;
    const savedObject = mode === 'create' ? { ...emptyObject, id: 0 } : currentObject;
    setHasUnsavedChanges(
      JSON.stringify(convertObjectToStringValues(formik.values)) !==
        JSON.stringify(convertObjectToStringValues(savedObject))
    );
  }, [JSON.stringify(formik.values)]);

  return (
    <CreateEditCard
      resetObject={resetObject}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      mode={mode}
      translationKey="products.categories"
    >
      <Box padding={2}>
        <CategoryForm
          formik={formik}
          cancelAction={() => {
            resetObject();
            removeSavedObject();
          }}
          objectId={objectId.toString()}
          isSubmitting={isLoading}
          errors={errors}
          clearError={clearError}
        />
        {hasUnsavedChanges && (
          <AlertMessage type="warning" locale={locale} theme={theme}>
            {t('errors.unsavedChanges')}
          </AlertMessage>
        )}
      </Box>
    </CreateEditCard>
  );
};

export default CreateEditCategoryCard;
